import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  deleteApiV1PerformedChoresById,
  getApiV1PerformedChores,
  type PerformedChoreDto,
} from "@/client";

export const useHistoryStore = defineStore("history", () => {
  const performedChores = ref<PerformedChoreDto[]>([]);
  const loading = ref(false);
  const deleting = ref(false);
  const error = ref<string | null>(null);

  async function fetchPerformedChores(fromDate?: Date, toDate?: Date) {
    loading.value = true;
    error.value = null;
    try {
      const params: { fromDate?: Date; toDate?: Date } = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await getApiV1PerformedChores({ query: params });

      if (response.error) {
        console.error("Error fetching performed chores:", response.error);
        error.value = "Failed to load performed chores";
        throw new Error("Failed to load performed chores");
      } else {
        performedChores.value = response.data!;
      }
    } finally {
      loading.value = false;
    }
  }

  async function deletePerformedChore(choreId: string) {
    deleting.value = true;
    error.value = null;
    try {
      const response = await deleteApiV1PerformedChoresById({
        path: { id: choreId },
      });
      if (response.error) {
        console.error("Error deleting performed chore:", response.error);
        error.value = "Failed to delete chore";
        throw new Error("Failed to delete chore");
      }
      // Remove the chore from the local store
      performedChores.value = performedChores.value.filter(
        (chore) => chore.id !== choreId,
      );

      return true;
    } finally {
      deleting.value = false;
    }
  }

  // Get chores grouped by date
  const choresByDate = computed(() => {
    const groups = new Map<string, PerformedChoreDto[]>();

    performedChores.value.forEach((chore) => {
      if (chore.performedAt) {
        const date = new Date(chore.performedAt).toDateString();
        if (!groups.has(date)) {
          groups.set(date, []);
        }
        groups.get(date)!.push(chore);
      }
    });

    // Convert to array and sort by date (newest first)
    return Array.from(groups.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, chores]) => ({
        date,
        chores: chores.sort(
          (a, b) =>
            new Date(b.performedAt!).getTime() -
            new Date(a.performedAt!).getTime(),
        ),
      }));
  });

  // Get total XP for the current period
  const totalXp = computed(() => {
    return performedChores.value.reduce((total, chore) => {
      return (
        total +
        (chore.participants?.reduce((choreTotal, participant) => {
          return choreTotal + (participant.totalXpAwarded || 0);
        }, 0) || 0)
      );
    }, 0);
  });

  // Get total number of chores performed
  const totalChores = computed(() => performedChores.value.length);

  // Get XP by user
  const xpByUser = computed(() => {
    const userXpMap = new Map<string, number>();

    performedChores.value.forEach((chore) => {
      chore.participants?.forEach((participant) => {
        if (participant.username && participant.totalXpAwarded) {
          const currentXp = userXpMap.get(participant.username) || 0;
          userXpMap.set(
            participant.username,
            currentXp + participant.totalXpAwarded,
          );
        }
      });
    });

    return Array.from(userXpMap.entries())
      .map(([username, xp]) => ({ username, xp }))
      .sort((a, b) => b.xp - a.xp);
  });

  // Get chores by category
  const choresByCategory = computed(() => {
    const categoryMap = new Map<string, { count: number; xp: number }>();

    performedChores.value.forEach((chore) => {
      const category = chore.categoryName || "Unknown";
      const current = categoryMap.get(category) || { count: 0, xp: 0 };
      const choreXp =
        chore.participants?.reduce(
          (total, p) => total + (p.totalXpAwarded || 0),
          0,
        ) || 0;

      categoryMap.set(category, {
        count: current.count + 1,
        xp: current.xp + choreXp,
      });
    });

    return Array.from(categoryMap.entries())
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.count - a.count);
  });

  function clearData() {
    performedChores.value = [];
    error.value = null;
  }

  return {
    // State
    performedChores,
    loading,
    deleting,
    error,

    // Computed
    choresByDate,
    totalXp,
    totalChores,
    xpByUser,
    choresByCategory,

    // Actions
    fetchPerformedChores,
    deletePerformedChore,
    clearData,
  };
});
